import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { FeaturesItem } from '../types';
import Image from 'next/image';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { IconChevronLeft } from '@tabler/icons-react';
import { getFeaturePriceBreakdown } from '@/utils/feature-price';
import { CurrencySign } from '../../common/CurrencySign';

type PaymentProps = {
  selectedServices: FeaturesItem[];
  onGoNext: () => void;
  fiatCost: number;
  fiatFakeCost: number;
  onClose: () => void;
  onGoBack: () => void;
};
const getDefaultIcon = (service: FeaturesItem) => {
  if (service.type === 'TopUp') {
    return '/assets/icons/monetization-rocket.svg';
  }
  if (service.type === 'Premium') {
    return '/assets/images/monetization-image-diamond.png';
  }

  return '/assets/icons/monetization-rocket.svg';
};

export const PaymentSummary = ({
  selectedServices,
  onGoNext,
  fiatCost,
  fiatFakeCost,
  onClose,
  onGoBack,
}: PaymentProps) => {
  const t = useTranslations('monetization');

  const discount = fiatFakeCost - fiatCost;
  const currencySign = getFeaturePriceBreakdown(selectedServices[0]).currencySign;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-xl'>
        <Button
          variant='ghost'
          size='icon'
          onClick={onGoBack}
          className='absolute top-4 start-4 z-10 h-8 w-8 rounded-full hover:bg-transparent'
        >
          <IconChevronLeft className='text-text-primary' />
        </Button>
        <div className='flex flex-col gap-[32px]'>
          <DialogHeader className='items-center text-center'>
            <DialogTitle>{t('payment_title')}</DialogTitle>
          </DialogHeader>
          <div className='flex flex-col'>
            {selectedServices.map((service) => {
              const price = getFeaturePriceBreakdown(service);
              return (
                <div key={service.id} className='flex py-[12px]'>
                  <div className=' py-[7px] px-[8px] bg-surface-light rounded-[4px] text-[14px] h-[35px] w-[35px]'>
                    {'icon' in service && service.icon ? (
                      <>
                        <Image
                          src={service.icon}
                          width={20}
                          height={20}
                          alt=''
                          className='self-start min-w-[20px] min-h-[20px]'
                        />
                      </>
                    ) : (
                      <Image
                        src={getDefaultIcon(service)}
                        width={20}
                        height={20}
                        alt=''
                        className='self-start min-w-[20px] min-h-[20px]'
                      />
                    )}
                  </div>

                  <div className='flex flex-col  justify-start ms-[12px] grow-2  max-h-[50px]'>
                    <div className='text=primary text-[16px] leading-[20px] font-medium'>{service.summaryTitle}</div>

                    <div className='text=primary text-[14px] leading-[17px] font-normal text-text-disabled-day'>
                      {service.summaryDescription}
                    </div>
                  </div>
                  <div className='flex gap-[4px] mt-[4px]'>
                    <div className='flex flex-col min-w-[40px]'>
                      <div className='text-[16px] leading-[24px] text-primary font-medium'>
                        {price.fiatAmount} <CurrencySign value={currencySign} />
                      </div>
                      <div
                        className={clsx('text-[14px] leading-[20px] text-secondary font-normal line-through', {
                          'opacity-0': price.fiatFakeAmount === price.fiatAmount || price.fiatFakeAmount === 0,
                        })}
                      >
                        {price.fiatFakeAmount} <CurrencySign value={currencySign} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div>
            <div className='w-full flex flex-col gap-[6px]'>
              <div className='flex items-center justify-between text-[16px]'>
                <span className='whitespace-nowrap '>{t('full_price')}</span>
                <span className='whitespace-nowrap'>
                  {fiatFakeCost} <CurrencySign value={currencySign} />
                </span>
              </div>

              <div className='flex items-center justify-between text-[16px]'>
                <span className='whitespace-nowrap '>{t('discount')}</span>
                <span className='whitespace-nowrap'>
                  {discount} <CurrencySign value={currencySign} />
                </span>
              </div>
              <div className='flex items-center  justify-between text-[18px] pt-[6px] border-t border-solid border-primary-stroke-day'>
                <span className='whitespace-nowrap font-medium'>{t('price_summary')}</span>
                <span className='whitespace-nowrap'>
                  {fiatCost} <CurrencySign value={currencySign} />
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className=' w-full start-0 px-[16px] pb-[16px] flex flex-col'>
          <Button disabled={fiatCost <= 0} onClick={onGoNext} className='w-full h-auto py-[10px]'>
            {t('continue')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
